import React, { useEffect, useRef, useState } from "react";
import { Modal } from "../../../components/ui/modal";
import Label from "../../../components/form/Label";
import Button from "../../../components/ui/button/Button";
import Select from "../../../components/form/Select";
import AddRemedialActionModal from "./AddRemedialActionModal";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";

const apiUrl = process.env.REACT_APP_API_URL;

type RemedialAction = {
  inspectionaction_id?: string | number;
  compinspectiontask_id?: string | number;
  compinspectionitem_id?: string | number;
  inspectiontask_id?: string | number;
  task_id?: string | number;
  task?: string;
  actiondate?: string;
  action_date?: string;
  name?: string;
  title?: string;
  status?: string;
  datecompleted?: string;
  date_completed?: string;
  completedby?: string;
  completed_by?: string;
  createdby?: string;
  created_by?: string;
};

type InspectionPhoto = {
  id: string;
  file?: File;
  previewUrl: string;
  name: string;
  isStored?: boolean;
};

type InspectionItemUpdatePayload = {
  inspection_id: string | number;
  compinspection_id: string | number;
  inspectiontask_id: string | number;
  status: number;
  remarks: string;
  cst?: string | number;
};

type ModalInspectionItemProps = {
  isOpen: boolean;
  onClose: () => void;
  onSaved?: () => Promise<void> | void;
  cst?: string | number | null;
  compInspectionId?: string | number | null;
  inspectionId?: string | number | null;
  inspectionItem?: {
    compinspectiontask_id?: string | number;
    compinspectionitem_id?: string | number;
    inspectiontask_id?: string | number;
    task_id?: string | number;
    inspection_id?: string | number;
    inspectionid?: string | number;
    task_name?: string;
    task_description?: string;
    heading_name?: string;
    status?: number;
    remarks?: string;
    actions?: RemedialAction[];
  };
};

export default function ModalInspectionItem({
  isOpen,
  onClose,
  onSaved,
  cst,
  compInspectionId,
  inspectionId,
  inspectionItem,
}: ModalInspectionItemProps) {
  const [activeTab, setActiveTab] = useState<"details" | "photos">("details");
  const [outcome, setOutcome] = useState<number | undefined>();
  const [remarks, setRemarks] = useState("");
  const [photos, setPhotos] = useState<InspectionPhoto[]>([]);
  const [photosByInspectionItem, setPhotosByInspectionItem] = useState<
    Record<string, InspectionPhoto[]>
  >({});
  const [savedPhotosByInspectionItem, setSavedPhotosByInspectionItem] = useState<
    Record<string, InspectionPhoto[]>
  >({});
  const [remedialActions, setRemedialActions] = useState<RemedialAction[]>([]);
  const [isLoadingActions, setIsLoadingActions] = useState(false);
  const [isAddActionModalOpen, setIsAddActionModalOpen] = useState(false);
  const [refreshActions, setRefreshActions] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const maxPhotoSlots = 3;
  const displayPhotos = photos
    .map((photo, index) => ({ photo, index }))
    .filter(
      ({ photo }) =>
        Boolean(photo.previewUrl) &&
        (photo.previewUrl.startsWith("blob:") || photo.previewUrl.startsWith("data:"))
    );
  const photosByInspectionItemRef = useRef<Record<string, InspectionPhoto[]>>({});
  const token = localStorage.getItem("token");
  const inspectionItemKey = [
    inspectionItem?.compinspectiontask_id,
    inspectionItem?.compinspectionitem_id,
    inspectionItem?.inspectiontask_id,
    inspectionItem?.task_id,
  ]
  
    .find((value) => value !== undefined && value !== null && value !== "")
    ?.toString();

  const isPresent = (value: unknown): value is string | number =>
    value !== undefined && value !== null && value !== "";

  useEffect(() => {
    if (inspectionItem) {
      setOutcome(inspectionItem.status);
      setRemarks(inspectionItem.remarks || "");
    }
  }, [inspectionItem, isOpen]);

  useEffect(() => {
    if (!inspectionItemKey) {
      setPhotos([]);
      return;
    }

    const localPhotos = photosByInspectionItem[inspectionItemKey] || [];
    const savedPhotos = savedPhotosByInspectionItem[inspectionItemKey] || [];
    setPhotos(localPhotos.length ? localPhotos : savedPhotos);
  }, [inspectionItemKey, photosByInspectionItem, savedPhotosByInspectionItem]);

  useEffect(() => {
    photosByInspectionItemRef.current = photosByInspectionItem;
  }, [photosByInspectionItem]);

  useEffect(() => {
    if (!isOpen || !inspectionItemKey) return;
    const resolvedInspectionId =
      inspectionItem?.inspection_id ?? inspectionItem?.inspectionid ?? inspectionId;
    const resolvedInspectionTaskId =
      inspectionItem?.inspectiontask_id ?? inspectionItem?.task_id;
    const resolvedCompInspectionId = compInspectionId;
    const fetchPhotos = async () => {
      try {
        if (!resolvedInspectionId && (!resolvedCompInspectionId || !resolvedInspectionTaskId)) {
          return;
        }
        const photoUrl = resolvedInspectionId
          ? `${apiUrl}/inspection_history/photos/${resolvedInspectionId}`
          : `${apiUrl}/inspection_history/photos/0?compinspection_id=${resolvedCompInspectionId}&inspectiontask_id=${resolvedInspectionTaskId}`;
        const response = await fetch(
          photoUrl,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              token: token || "",
            },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const result = await response.json();
        console.log(result);
        
        const serverPhotos = Array.isArray(result?.photos) ? result.photos : [];
        const mapped = serverPhotos.map((photo: { index: number; mime: string; base64: string }) => ({
          id: `stored-${resolvedInspectionId}-${photo.index}`,
          previewUrl: `data:${photo.mime};base64,${photo.base64}`,
          name: `Photo ${photo.index + 1}`,
          isStored: true,
        }));

        setSavedPhotosByInspectionItem((prev) => ({
          ...prev,
          [inspectionItemKey]: mapped,
        }));
      } catch (error) {
        console.error("Error fetching inspection photos:", error);
      }
    };

    fetchPhotos();
  }, [isOpen, inspectionItemKey, inspectionItem, inspectionId, token, compInspectionId]);

  useEffect(() => {
    if (isOpen) {
      setSaveError("");
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !inspectionItem) return;
    const selectedIds = [
      inspectionItem.compinspectiontask_id,
      inspectionItem.compinspectionitem_id,
      inspectionItem.inspectiontask_id,
      inspectionItem.task_id,
    ].filter((id) => id !== undefined && id !== null && id !== "");
    const selectedTaskName = (inspectionItem.task_name || "").trim().toLowerCase();

    const actionMatchesSelectedItem = (action: RemedialAction) => {
      const actionIds = [
        action.compinspectiontask_id,
        action.compinspectionitem_id,
        action.inspectiontask_id,
        action.task_id,
      ].filter((id) => id !== undefined && id !== null && id !== "");

      if (selectedIds.length > 0 && actionIds.length > 0) {
        return actionIds.some((id) =>
          selectedIds.some((selectedId) => String(selectedId) === String(id))
        );
      }

      if (selectedTaskName) {
        return (action.task || "").trim().toLowerCase() === selectedTaskName;
      }

      return false;
    };

    const fetchRemedialActions = async () => {
      setIsLoadingActions(true);
      setRemedialActions([]);
      try {
        if (!compInspectionId) {
          setRemedialActions([]);
          return;
        }

        const response = await fetch(`${apiUrl}/inspection_history/action/${compInspectionId}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            token: token || "",
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const result = await response.json();
        const actions = Array.isArray(result?.remedialActions) ? result.remedialActions : [];
        setRemedialActions(actions.filter(actionMatchesSelectedItem));
      } catch (error) {
        console.error("Error fetching remedial actions:", error);
        setRemedialActions([]);
      } finally {
        setIsLoadingActions(false);
      }
    };

    fetchRemedialActions();
  }, [isOpen, inspectionItem, compInspectionId, refreshActions]);

  const handleSave = async () => {
    if (!compInspectionId) {
      setSaveError("Missing compinspection_id.");
      return;
    }

    if (!inspectionItem) {
      setSaveError("Inspection item is missing.");
      return;
    }

    if (outcome !== 1 && outcome !== 2) {
      setSaveError("Please select an inspection item outcome.");
      return;
    }

    const token = localStorage.getItem("token");

    const resolvedInspectionId =
      inspectionItem.inspection_id ?? inspectionItem.inspectionid ?? inspectionId;
    const resolvedInspectionTaskId =
      inspectionItem.inspectiontask_id ?? inspectionItem.task_id;
    const resolvedCompInspectionTaskId =
      inspectionItem.compinspectiontask_id ?? inspectionItem.compinspectionitem_id;

    if (!isPresent(resolvedInspectionId)) {
      setSaveError("Missing inspection_id.");
      return;
    }
    if (!isPresent(resolvedInspectionTaskId)) {
      setSaveError("Missing inspectiontask_id.");
      return;
    }

    const payload: InspectionItemUpdatePayload = {
      inspection_id: resolvedInspectionId,
      compinspection_id: compInspectionId,
      inspectiontask_id: resolvedInspectionTaskId,
      status: outcome,
      remarks: remarks.trim(),
    };

    try {
      setIsSaving(true);
      setSaveError("");

      let response = await fetch(`${apiUrl}/inspection_history/items`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          token: token || "",
        },
        body: JSON.stringify(payload),
      });

      if (response.status === 404) {
        response = await fetch(`${apiUrl}/inspection-history/items`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            token: token || "",
          },
          body: JSON.stringify(payload),
        });
      }

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      if (photos.length > 0) {
        const photoFormData = new FormData();
        photoFormData.append("inspection_id", String(resolvedInspectionId));
        photoFormData.append("compinspection_id", String(compInspectionId));
        photoFormData.append("inspectiontask_id", String(resolvedInspectionTaskId));

        if (resolvedCompInspectionTaskId !== undefined && resolvedCompInspectionTaskId !== null) {
          photoFormData.append(
            "compinspectiontask_id",
            String(resolvedCompInspectionTaskId)
          );
        }

        if (cst !== undefined && cst !== null && cst !== "") {
          photoFormData.append("cst", String(cst));
        }

        const uploadFiles = (
          await Promise.all(
            photos.map(async (photo, index) => {
              if (photo.file) {
                return photo.file;
              }
              if (photo.previewUrl.startsWith("data:")) {
                const blob = await fetch(photo.previewUrl).then((res) => res.blob());
                const ext = blob.type.split("/")[1] || "jpg";
                const name = photo.name || `photo-${index + 1}.${ext}`;
                return new File([blob], name, { type: blob.type || "image/jpeg" });
              }
              return null;
            })
          )
        ).filter((file): file is File => Boolean(file));

        uploadFiles.forEach((file) => {
          photoFormData.append("photos", file);
        });

        const photoResponse = await fetch(`${apiUrl}/inspection_history/photos`, {
          method: "PUT",
          headers: {
            // Let the browser set the correct multipart/form-data boundary.
            token: token || "",
          },
          body: photoFormData,
        });

        if (!photoResponse.ok) {
          const photoErrorText = await photoResponse.text();
          throw new Error(`Photo upload failed: HTTP ${photoResponse.status}: ${photoErrorText}`);
        }
      }

      await onSaved?.();
      onClose();
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to save inspection item outcome";
      setSaveError(message);
      console.error("Error saving inspection item outcome:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const selectedFiles = Array.from(e.target.files).map((file) => ({
      id: `${file.name}-${file.lastModified}-${Math.random().toString(36).slice(2)}`,
      file,
      previewUrl: URL.createObjectURL(file),
      name: file.name,
    }));

    setPhotos((prev) => {
      const updatedPhotos = [...prev, ...selectedFiles];

      if (inspectionItemKey) {
        setPhotosByInspectionItem((prevMap) => ({
          ...prevMap,
          [inspectionItemKey]: updatedPhotos,
        }));
      }

      return updatedPhotos;
    });

    e.target.value = "";
  };

  const removePhoto = (index: number) => {
    setPhotos((prev) => {
      const photoToRemove = prev[index];
      if (photoToRemove) {
        if (photoToRemove.previewUrl.startsWith("blob:")) {
          URL.revokeObjectURL(photoToRemove.previewUrl);
        }
      }

      const updatedPhotos = prev.filter((_, i) => i !== index);

      if (inspectionItemKey) {
        setPhotosByInspectionItem((prevMap) => ({
          ...prevMap,
          [inspectionItemKey]: updatedPhotos,
        }));
      }

      return updatedPhotos;
    });
  };

  useEffect(() => {
    return () => {
      Object.values(photosByInspectionItemRef.current)
        .flat()
        .forEach((photo) => {
          if (photo.previewUrl.startsWith("blob:")) {
            URL.revokeObjectURL(photo.previewUrl);
          }
        });
    };
  }, []);

  const formatDate = (value?: string) => {
    if (!value || value.startsWith("1900-01-01")) return "-";
    const date = new Date(value.replace(/Z$/, ""));
    return date.toLocaleDateString("en-AU", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        showCloseButton={false}
        className="max-w-6xl p-6"
      >
        {/*HEADER*/}
        <div className="flex justify-between items-center mb-4">
          <h4 className="text-lg font-semibold">
            Inspection Item: {inspectionItem?.task_name || ""}
          </h4>
          <Button onClick={() => setIsAddActionModalOpen(true)}>
            Add New Remedial Action
          </Button>
        </div>

        {/* TABS */}
        <div className="flex gap-2 mb-4 border-b">
          <button
            onClick={() => setActiveTab("details")}
            className={`px-4 py-2 text-sm font-medium rounded-t
              ${
                activeTab === "details"
                  ? "bg-white border border-b-0 text-gray-900"
                  : "bg-gray-100 text-gray-500 hover:text-gray-700"
              }`}
          >
            Inspection Item Details
          </button>

          <button
            onClick={() => setActiveTab("photos")}
            className={`px-4 py-2 text-sm font-medium rounded-t
              ${
                activeTab === "photos"
                  ? "bg-white border border-b-0 text-gray-900"
                  : "bg-gray-100 text-gray-500 hover:text-gray-700"
              }`}
          >
            Inspection Item Photos
          </button>
        </div>

        {/* TAB CONTENT */}
        {activeTab === "details" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* LEFT - DETAILS */}
            <div className="space-y-4">
              <div>
                <Label>Heading: {inspectionItem?.heading_name || ""}</Label>
                <textarea
                  className="w-full h-32 rounded-lg border px-3 py-2 text-sm"
                  value={inspectionItem?.task_description || ""}
                  readOnly
                />
              </div>

              <div>
                <Label>Inspection Item Outcome</Label>
                <Select
                  options={[
                    { value: "1", label: "Satisfactory" },
                    { value: "2", label: "Unsatisfactory" },
                  ]}
                  placeholder="Select Outcome"
                  className="mt-2 bg-gray-50 dark:bg-gray-800"
                  value={outcome ? outcome.toString() : ""}
                  onChange={(val) => setOutcome(parseInt(val, 10))}
                />
              </div>

              <div>
                <Label>Inspection Item Remarks</Label>
                <textarea
                  className="w-full h-24 rounded-lg border px-3 py-2 text-sm"
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  placeholder="Add new remark..."
                />
              </div>
            </div>

            {/* RIGHT - REMEDIAL ACTIONS */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <Label>Remedial Actions</Label>
              </div>
              <div className="border rounded-lg h-56 overflow-y-auto overflow-x-auto">
                <Table className="w-full text-xs border-collapse">
                  <TableHeader className="bg-[#d9ffa3]">
                    <TableRow>
                      <TableCell isHeader className="px-2 py-1 border">
                        Action Date
                      </TableCell>
                      <TableCell isHeader className="px-2 py-1 border">
                        Action Title
                      </TableCell>
                      <TableCell isHeader className="px-2 py-1 border">
                        Status
                      </TableCell>
                      <TableCell isHeader className="px-2 py-1 border">
                        Date Completed
                      </TableCell>
                      <TableCell isHeader className="px-2 py-1 border">
                        Completed By
                      </TableCell>
                      <TableCell isHeader className="px-2 py-1 border">
                        Created By
                      </TableCell>
                    </TableRow>
                  </TableHeader>

                  <TableBody>
                    {isLoadingActions ? (
                      <tr>
                        <td colSpan={6} className="text-center py-6 text-gray-400">
                          Loading...
                        </td>
                      </tr>
                    ) : remedialActions.length ? (
                      remedialActions.map((a, i) => (
                        <TableRow
                          key={a.inspectionaction_id || i}
                          className="cursor-pointer hover:bg-[#ffffa3]"
                        >
                          <TableCell className="border px-2 py-1">
                            {formatDate(a.actiondate || a.action_date)}
                          </TableCell>
                          <TableCell className="border px-2 py-1">
                            {a.name || a.title || "-"}
                          </TableCell>
                          <TableCell className="border px-2 py-1">
                            {a.status || "-"}
                          </TableCell>
                          <TableCell className="border px-2 py-1">
                            {formatDate(a.datecompleted || a.date_completed)}
                          </TableCell>
                          <TableCell className="border px-2 py-1">
                            {a.completedby || a.completed_by || "-"}
                          </TableCell>
                          <TableCell className="border px-2 py-1">
                            {a.createdby || a.created_by || "-"}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6} className="text-center py-6 text-gray-400">
                          No records to view
                        </td>
                      </tr>
                    )}
                  </TableBody>
                </Table>
              </div>
              <div className="mt-4">
                <Label>Inspection Item Remarks History</Label>
                <textarea
                  className="w-full h-24 rounded-lg border px-3 py-2 text-sm"
                  readOnly
                />
              </div>
            </div>
          </div>
        )}

        {/* PHOTOS TAB */}
        {activeTab === "photos" && (
          <div className="border rounded-lg p-6 bg-gray-50 min-h-[420px]">
            {displayPhotos.length === 0 ? (
              <div className="flex h-48 flex-col items-center justify-center gap-3 text-sm text-gray-500">
                <div>No photos uploaded yet.</div>
                <label className="cursor-pointer text-sm text-brand-600 underline">
                  Add Picture
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handlePhotoUpload}
                  />
                </label>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                {displayPhotos.slice(0, maxPhotoSlots).map(({ photo, index }) => (
                  <div key={photo.id} className="rounded-lg border bg-white p-3">
                    <Label>Inspection Item Photo</Label>
                    <div className="mt-3">
                      <img
                        src={photo.previewUrl}
                        alt={`Inspection item photo ${index + 1}`}
                        className="h-32 w-full rounded border object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => removePhoto(index)}
                        className="mt-2 text-sm text-brand-600 underline"
                      >
                        Remove Picture
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {displayPhotos.length > 0 && displayPhotos.length < maxPhotoSlots && (
              <div className="mt-4">
                <label className="cursor-pointer text-sm text-brand-600 underline">
                  Add Picture
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handlePhotoUpload}
                  />
                </label>
              </div>
            )}
          </div>
        )}

        {/* FOOTER */}
        {saveError && (
          <div className="mt-4 rounded-lg border border-error-300 bg-error-50 px-3 py-2 text-sm text-error-700">
            {saveError}
          </div>
        )}
        <div className="flex justify-end gap-3 mt-6">
          <Button size="sm" variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button size="sm" onClick={handleSave} disabled={isSaving}>
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </div>
      </Modal>
      <AddRemedialActionModal
        isOpen={isAddActionModalOpen}
        onClose={() => setIsAddActionModalOpen(false)}
        cst={cst}
        compInspectionId={compInspectionId}
        inspectionId={inspectionId}
        inspectionItem={inspectionItem}
        onSaved={() => setRefreshActions((prev) => prev + 1)}
      />
    </>
  );
}
